import React, { useState, useEffect } from 'react';
import { collection, doc, setDoc, updateDoc, getDoc, getDocs, deleteDoc, onSnapshot, query, orderBy, limit } from 'firebase/firestore';
import { db } from '../firebase';
import { useTheme } from './ThemeProvider';
import { GamerCard } from './FindTeamPage';
import AdminTournamentSlots from './AdminTournamentSlots';

export default function AdminPanel() {
    const { theme, toggleTheme } = useTheme();
    const [activeTab, setActiveTab] = useState('findTeammates'); // findTeammates, bgmiAccounts, mobileMatches, emulatorMatches, miscellaneous
    const [miscTab, setMiscTab] = useState('staffApplications'); // staffApplications, playerReports, paymentIssues, transactions
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [txnSearch, setTxnSearch] = useState('');
    const [txnFilter, setTxnFilter] = useState('ALL');
    
    // Hall of Fame States
    const [hallOfFameData, setHallOfFameData] = useState([]); // This stores the current players IN the Hall of Fame
    const [hallOfShameData, setHallOfShameData] = useState([]); // This stores the current players IN the Hall of Shame
    const [allGamersList, setAllGamersList] = useState([]);
    const [isFetchingAllGamers, setIsFetchingAllGamers] = useState(false);
    
    // For matches
    const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
    const [matchesData, setMatchesData] = useState(null);
    const [selectedMatchForSlots, setSelectedMatchForSlots] = useState(null);

    // Ban Modal States
    const [banModalOpen, setBanModalOpen] = useState(false);
    const [userToBan, setUserToBan] = useState(null);
    const [banReason, setBanReason] = useState("");

    // Edit User Modal States
    const [editUserModalOpen, setEditUserModalOpen] = useState(false);
    const [userToEdit, setUserToEdit] = useState(null);
    const [editUserData, setEditUserData] = useState({
        name: '',
        mobile: '',
        upiId: '',
        gameUid1: '',
        gameIgn1: '',
        gameUid2: '',
        gameIgn2: ''
    });
    const [isFetchingUid1, setIsFetchingUid1] = useState(false);
    const [isFetchingUid2, setIsFetchingUid2] = useState(false);

    const handleFetchUidAdmin = async (slot) => {
        const uidToFetch = slot === 1 ? editUserData.gameUid1 : editUserData.gameUid2;
        if (!uidToFetch || uidToFetch.length < 5) {
            alert("Please enter a valid Game UID first.");
            return;
        }

        if (editUserData.gameUid1 && editUserData.gameUid2 && editUserData.gameUid1 === editUserData.gameUid2) {
            alert("Game UID 1 and Game UID 2 cannot be the same.");
            return;
        }

        const isDuplicate = allGamersList.some(gamer => 
            gamer._id !== userToEdit._id && 
            (gamer.gameUid1 === uidToFetch || gamer.gameUid2 === uidToFetch || gamer.profileData?.gameUid1 === uidToFetch || gamer.profileData?.gameUid2 === uidToFetch)
        );

        if (isDuplicate) {
            alert("This Game UID is already registered by another gamer.");
            return;
        }

        if (slot === 1) setIsFetchingUid1(true);
        else setIsFetchingUid2(true);
        
        try {
            const response = await fetch(`https://bgmiesports.gridlabsproduction.workers.dev/?uid=${uidToFetch}`);
            const data = await response.json();
            
            if (data.success) {
                if (slot === 1) {
                    setEditUserData(prev => ({...prev, gameIgn1: data.username}));
                } else {
                    setEditUserData(prev => ({...prev, gameIgn2: data.username}));
                }
            } else {
                throw new Error(data.error || "FAILED TO FETCH.");
            }
        } catch (error) {
            console.error("Error fetching UID:", error);
            alert("Failed to fetch IGN. Please try again or check UID.");
        } finally {
            if (slot === 1) setIsFetchingUid1(false);
            else setIsFetchingUid2(false);
        }
    };

    // Editing modal
    const [editingItem, setEditingItem] = useState(null);
    const [editFormState, setEditFormState] = useState(null);
    const [saveLoading, setSaveLoading] = useState(false);
    const [startingMatchIndex, setStartingMatchIndex] = useState(null);
    const [livestreamUrlInput, setLivestreamUrlInput] = useState('');

    // ID/PASS Modal States
    const [idPassModalOpen, setIdPassModalOpen] = useState(false);
    const [matchForIdPass, setMatchForIdPass] = useState(null);
    const [idPassData, setIdPassData] = useState({ roomId: '', roomPassword: '' });

    const handlePublishIdPass = async () => {
        if (!matchForIdPass) return;
        
        let newArray = [...matchesData];
        const matchIndex = matchForIdPass.index;
        
        newArray[matchIndex] = {
            ...newArray[matchIndex],
            roomId: idPassData.roomId,
            roomPassword: idPassData.roomPassword
        };
        
        try {
            if (activeTab === 'matches') { await saveCombinedMatches(newArray); } else { await setDoc(doc(db, 'dailyMatches', activeTab, 'dates', selectedDate), { data: newArray }); }
            if (editFormState && Array.isArray(editFormState)) {
                let newEditFormState = [...editFormState];
                if (editingItem && editingItem._isSingleMatch) {
                    newEditFormState[0] = { ...newEditFormState[0], roomId: idPassData.roomId, roomPassword: idPassData.roomPassword };
                } else {
                    newEditFormState[matchIndex] = { ...newEditFormState[matchIndex], roomId: idPassData.roomId, roomPassword: idPassData.roomPassword };
                }
                setEditFormState(newEditFormState);
            }
            alert("Credentials Published Successfully!");
            setIdPassModalOpen(false);
        } catch (error) {
            console.error(error);
            alert("Error publishing credentials!");
        }
    };

    const handleToggleReadyCheck = async (matchIndex) => {
        let newArray = [...matchesData];
        newArray[matchIndex] = {
            ...newArray[matchIndex],
            isReadyCheckActive: !newArray[matchIndex].isReadyCheckActive
        };
        try {
            if (activeTab === 'matches') { await saveCombinedMatches(newArray); } else { await setDoc(doc(db, 'dailyMatches', activeTab, 'dates', selectedDate), { data: newArray }); }
            if (editFormState && Array.isArray(editFormState)) {
                let newEditFormState = [...editFormState];
                if (editingItem && editingItem._isSingleMatch) {
                    newEditFormState[0] = { ...newEditFormState[0], isReadyCheckActive: newArray[matchIndex].isReadyCheckActive };
                } else {
                    newEditFormState[matchIndex] = { ...newEditFormState[matchIndex], isReadyCheckActive: newArray[matchIndex].isReadyCheckActive };
                }
                setEditFormState(newEditFormState);
            }
        } catch (error) {
            console.error("Error toggling ready check:", error);
            alert("Error toggling ready check!");
        }
    };

    const handleDateChange = (offset) => {
        if (!selectedDate) return;
        const [y, m, d] = selectedDate.split('-');
        const current = new Date(y, m - 1, d);
        current.setDate(current.getDate() + offset);
        const year = current.getFullYear();
        const month = String(current.getMonth() + 1).padStart(2, '0');
        const day = String(current.getDate()).padStart(2, '0');
        setSelectedDate(`${year}-${month}-${day}`);
    };

    useEffect(() => {
        const actualTab = activeTab === 'miscellaneous' ? miscTab : activeTab;
        if (actualTab === 'findTeammates' || actualTab === 'bgmiAccounts' || actualTab === 'staffApplications' || actualTab === 'playerReports' || actualTab === 'paymentIssues' || actualTab === 'transactions') {
            setLoading(true);
            const collRef = collection(db, actualTab);
            const unsubscribe = onSnapshot(collRef, (snapshot) => {
                const fetchedData = [];
                snapshot.forEach(docSnap => {
                    if (actualTab === 'findTeammates' && docSnap.id.startsWith('TM_')) return;
                    fetchedData.push({ _id: docSnap.id, ...docSnap.data() });
                });
                
                // Sort by timestamp descending if timestamp exists
                fetchedData.sort((a, b) => {
                    if (a.timestamp && b.timestamp) {
                        return b.timestamp.seconds - a.timestamp.seconds;
                    }
                    return 0;
                });
                
                setData(fetchedData);
                setLoading(false);
            }, (err) => {
                console.error(err);
                setLoading(false);
            });
            return () => unsubscribe();
        } else if (actualTab === 'hallOfFame') {
            const q = query(collection(db, 'leaderboard'), orderBy('earnings', 'desc'), limit(100));
            const unsubscribe = onSnapshot(q, (snapshot) => {
                const items = [];
                snapshot.forEach((doc) => {
                    items.push({ _id: doc.id, ...doc.data() });
                });
                setHallOfFameData(items);
            }, (error) => {
                console.error("Error fetching leaderboard:", error);
            });

            const qShame = query(collection(db, 'shameboard'), orderBy('lastUpdated', 'desc'), limit(100));
            const unsubscribeShame = onSnapshot(qShame, (snapshot) => {
                const items = [];
                snapshot.forEach((doc) => {
                    items.push({ _id: doc.id, ...doc.data() });
                });
                setHallOfShameData(items);
            }, (error) => {
                console.error("Error fetching shameboard:", error);
            });

            // Listen to all gamers and fetch their matchesData real-time
            const unsubscribeUsers = onSnapshot(collection(db, 'users'), async (usersSnap) => {
                setIsFetchingAllGamers(true);
                try {
                    const allGamersPromises = usersSnap.docs.map(async (userDoc) => {
                        if (userDoc.id.startsWith('TM_')) return null; // Skip non-user docs
                        const userData = userDoc.data();
                        const matchesRef = doc(db, 'users', userDoc.id, 'transactions', 'matchesData');
                        const matchesSnap = await getDoc(matchesRef);
                        
                        let matchesPlayed = 0;
                        let xp = 0;
                        let earnings = 0;
                        if (matchesSnap.exists()) {
                            const mData = matchesSnap.data();
                            matchesPlayed = mData.combatRecords?.totalMatches || 0;
                            xp = mData.combatRecords?.lifetimeXP || 0;
                            earnings = mData.bountyVault?.tournamentPayouts || 0;
                        }
                        
                        return {
                            _id: userDoc.id,
                            uid: userDoc.id,
                            name: userData.profileData?.name || userData.name || "GAMER",
                            gameUid1: userData.gameUid1 || userData.profileData?.gameUid1 || null,
                            gameIgn1: userData.gameIgn1 || userData.profileData?.gameIgn1 || null,
                            gameUid2: userData.gameUid2 || userData.profileData?.gameUid2 || null,
                            gameIgn2: userData.gameIgn2 || userData.profileData?.gameIgn2 || null,
                            mobile: userData.mobile || userData.profileData?.mobile || null,
                            upiId: userData.upiId || userData.profileData?.upiId || null,
                            playerId: userData.playerId || userData.profileData?.playerId || `GLPC${userDoc.id.substring(0, 6).toUpperCase()}`,
                            isBanned: userData.isBanned || false,
                            preBanTrustScore: userData.preBanTrustScore || null,
                            trustScore: userData.profileData?.trustScore !== undefined ? userData.profileData.trustScore : 100,
                            matchesPlayed,
                            xp,
                            earnings
                        };
                    });
                    
                    let gamersResults = await Promise.all(allGamersPromises);
                    gamersResults = gamersResults.filter(g => g !== null);
                    gamersResults.sort((a, b) => b.earnings - a.earnings || b.xp - a.xp);
                    setAllGamersList(gamersResults);
                } catch (error) {
                    console.error("Error fetching all gamers:", error);
                } finally {
                    setIsFetchingAllGamers(false);
                }
            });

            return () => {
                unsubscribe();
                unsubscribeShame();
                unsubscribeUsers();
            };
        } else if (activeTab === 'matches') {
            setLoading(true);
            
            let mobileData = [];
            let emulatorData = [];
            let mLoaded = false;
            let eLoaded = false;

            const updateCombined = () => {
                if (mLoaded && eLoaded) {
                    const combined = [
                        ...mobileData.map(m => ({ ...m, platform: m.platform || 'MOBILE' })),
                        ...emulatorData.map(m => ({ ...m, platform: m.platform || 'EMULATOR' }))
                    ];
                    setMatchesData(combined);
                    setLoading(false);
                }
            };

            const unsubMobile = onSnapshot(doc(db, 'dailyMatches', 'mobileMatches', 'dates', selectedDate), (snap) => {
                mobileData = snap.exists() ? snap.data().data || [] : [];
                mLoaded = true;
                updateCombined();
            }, (err) => { console.error(err); mLoaded = true; updateCombined(); });

            const unsubEmulator = onSnapshot(doc(db, 'dailyMatches', 'emulatorMatches', 'dates', selectedDate), (snap) => {
                emulatorData = snap.exists() ? snap.data().data || [] : [];
                eLoaded = true;
                updateCombined();
            }, (err) => { console.error(err); eLoaded = true; updateCombined(); });

            return () => {
                unsubMobile();
                unsubEmulator();
            };
        }
    }, [activeTab, miscTab, selectedDate]);

    const handleEdit = (item) => {
        setEditingItem(item);
        let copy = JSON.parse(JSON.stringify(item));
        if (copy._id && !Array.isArray(copy)) delete copy._id;
        
        if (Array.isArray(copy)) {
            copy = copy.map(match => ({ lobbyType: 'CLEAN LOBBY', ...match }));
        }
        
        setEditFormState(copy);
    };

    const handleEditSingleMatch = (index) => {
        const matchData = { lobbyType: 'CLEAN LOBBY', ...matchesData[index] };
        setEditingItem({ _index: index, _isSingleMatch: true, ...matchData });
        setEditFormState([{ ...matchData }]);
    };

    const handleFieldChange = (key, value, index = null) => {
        if (index !== null) {
            const newArray = [...editFormState];
            newArray[index] = { ...newArray[index], [key]: value };
            setEditFormState(newArray);
        } else {
            setEditFormState({ ...editFormState, [key]: value });
        }
    };


    // State for drag and drop sorting
    const [draggedIndex, setDraggedIndex] = useState(null);
    const [dragEnabledIndex, setDragEnabledIndex] = useState(null);

    const handleDragStart = (e, index) => {
        if (dragEnabledIndex !== index) {
            e.preventDefault();
            return;
        }
        setDraggedIndex(index);
        e.dataTransfer.effectAllowed = 'move';
        // Optional: reduce opacity of dragged item
        e.currentTarget.style.opacity = '0.5';
    };

    const handleDragOver = (e, index) => {
        e.preventDefault();
        if (draggedIndex === null || draggedIndex === index) return;
        
        const newArray = [...editFormState];
        const draggedItem = newArray[draggedIndex];
        newArray.splice(draggedIndex, 1);
        newArray.splice(index, 0, draggedItem);
        
        setDraggedIndex(index);
        setEditFormState(newArray);
    };

    const handleDragEnd = (e) => {
        e.currentTarget.style.opacity = '1';
        setDraggedIndex(null);
        setDragEnabledIndex(null);
    };

    const resequenceMatches = (array) => {
        return array.map((m, i) => ({
            ...m,
            id: i + 1
        }));
    };

    const saveCombinedMatches = async (newArray) => {
        const mobileMatches = resequenceMatches(newArray.filter(m => m.platform !== 'EMULATOR'));
        const emulatorMatches = resequenceMatches(newArray.filter(m => m.platform === 'EMULATOR'));
        
        await setDoc(doc(db, 'dailyMatches', 'mobileMatches', 'dates', selectedDate), { data: mobileMatches });
        await setDoc(doc(db, 'dailyMatches', 'mobileMatches'), { exists: true }, { merge: true });
        
        await setDoc(doc(db, 'dailyMatches', 'emulatorMatches', 'dates', selectedDate), { data: emulatorMatches });
        await setDoc(doc(db, 'dailyMatches', 'emulatorMatches'), { exists: true }, { merge: true });
    };

const handleDeleteMatch = (index) => {
        if (!window.confirm("Are you sure you want to delete this match?")) return;
        let newArray = [...editFormState];
        newArray.splice(index, 1);
        newArray = resequenceMatches(newArray);
        setEditFormState(newArray);
    };

    const handleDeleteMatchDirect = async (index) => {
        if (!window.confirm("Are you sure you want to delete this match?")) return;
        let newArray = [...(matchesData || [])];
        newArray.splice(index, 1);
        newArray = resequenceMatches(newArray);
        try {
            if (activeTab === 'matches') { await saveCombinedMatches(newArray); } else { await setDoc(doc(db, 'dailyMatches', activeTab, 'dates', selectedDate), { data: newArray }); }
        } catch (error) {
            console.error(error);
            alert("Error deleting match!");
        }
    };

    const handleConcludeMatch = async (index) => {
        if (!window.confirm("Conclude this match? This will mark it as completed and download the Results JSON template.")) return;
        let newArray = [...matchesData];
        let matchToConclude = newArray[index];
        const uniqueMatchId = matchToConclude.matchId || Math.floor(10000000 + Math.random() * 90000000).toString();
        matchToConclude = { ...matchToConclude, isCompleted: true, isLiveNow: false, matchId: uniqueMatchId };
        newArray[index] = matchToConclude;
        
        try {
            if (activeTab === 'matches') { await saveCombinedMatches(newArray); } else { await setDoc(doc(db, 'dailyMatches', activeTab, 'dates', selectedDate), { data: newArray }); }
            if (editFormState && Array.isArray(editFormState)) {
                let newEditFormState = [...editFormState];
                if (editingItem && editingItem._isSingleMatch) {
                    newEditFormState[0] = { ...newEditFormState[0], isCompleted: true, isLiveNow: false, matchId: uniqueMatchId };
                } else {
                    newEditFormState[index] = { ...newEditFormState[index], isCompleted: true, isLiveNow: false, matchId: uniqueMatchId };
                }
                setEditFormState(newEditFormState);
            }
            
            // Generate and Download JSON Template
            const bookedSlots = matchToConclude.bookedSlots || [];
            const teamsMap = new Map();
            bookedSlots.forEach(slot => {
                if (!teamsMap.has(slot.teamIndex)) {
                    teamsMap.set(slot.teamIndex, {
                        teamId: slot.teamIndex,
                        teamPosition: slot.teamIndex, // Default starting position
                        players: []
                    });
                }
                teamsMap.get(slot.teamIndex).players.push({
                    kills: 0,
                    ign: slot.ign || "UNKNOWN",
                    uid: slot.uid || "UNKNOWN"
                });
            });
            
            const jsonTemplate = Array.from(teamsMap.values());
            const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(jsonTemplate, null, 2));
            const downloadAnchorNode = document.createElement('a');
            downloadAnchorNode.setAttribute("href", dataStr);
            downloadAnchorNode.setAttribute("download", `${matchToConclude.title.replace(/[^a-z0-9]/gi, '_').toUpperCase()}_ID_${uniqueMatchId}_RESULTS.json`);
            document.body.appendChild(downloadAnchorNode);
            downloadAnchorNode.click();
            downloadAnchorNode.remove();
            
        } catch (error) {
            console.error(error);
            alert("Error concluding match!");
        }
    };

    const handleToggleDummyTesting = async (index) => {
        if (!window.confirm("Toggle Dummy Testing mode for this match?")) return;
        let newArray = [...matchesData];
        newArray[index] = { ...newArray[index], isDummyTesting: !newArray[index].isDummyTesting };
        try {
            if (activeTab === 'matches') { await saveCombinedMatches(newArray); } else { await setDoc(doc(db, 'dailyMatches', activeTab, 'dates', selectedDate), { data: newArray }); }
        } catch (error) {
            console.error(error);
            alert("Error updating match!");
        }
    };

    const handleTournamentResultClick = (match) => {
        const docId = match.platform === 'EMULATOR' ? 'emulatorMatches' : 'mobileMatches';
        window.dispatchEvent(new CustomEvent('openTournamentResult', { detail: { ...match, isAdminView: true, docId, dateStr: selectedDate } }));
    };

    const handleAddMatch = async (platform = 'MOBILE') => {
        let newArray = matchesData ? [...matchesData] : [];
        const newId = newArray.length + 1;
        const lobbyPrefix = platform === 'EMULATOR' ? 'EMULATOR' : 'MOBILE';
        
        // Calculate time 30 mins from now rounded to nearest 30 mins
        const now = new Date();
        now.setMinutes(now.getMinutes() + 30);
        const remainder = now.getMinutes() % 30;
        if (remainder !== 0) {
            now.setMinutes(now.getMinutes() + (30 - remainder));
        }
        let h = now.getHours();
        let m = now.getMinutes() === 0 ? '00' : '30';
        let ampm = h >= 12 ? 'PM' : 'AM';
        let displayH = h % 12;
        if (displayH === 0) displayH = 12;
        let timeStr = `${displayH.toString().padStart(2, '0')}:${m} ${ampm}`;

        newArray.push({
            id: newId,
            title: `${lobbyPrefix} #${newId}`,
            map: "Erangel",
            mode: "Squad",
            entry: "16 GLPC",
            prize: "₹ 1000",
            killReward: "₹ 5",
            slots: "0 / 100",
            time: timeStr,
            isCompleted: false,
            lobbyType: 'CLEAN LOBBY',
            roomId: '',
            roomPassword: '',
            platform: platform
        });
        newArray = resequenceMatches(newArray);
        try {
            if (activeTab === 'matches') { await saveCombinedMatches(newArray); }
        } catch (error) {
            console.error(error);
            alert("Error adding match!");
        }
    };

    const handleDelete = async (item) => {
        if (!window.confirm("Are you sure you want to delete this?")) return;
        try {
            const actualTab = activeTab === 'miscellaneous' ? miscTab : activeTab;
            if (actualTab === 'findTeammates' || actualTab === 'bgmiAccounts' || actualTab === 'staffApplications' || actualTab === 'playerReports' || actualTab === 'paymentIssues') {
                await deleteDoc(doc(db, actualTab, item._id));
            }
        } catch (error) {
            console.error(error);
            alert("Error deleting!");
        }
    };

    const handleStatusUpdate = async (item, newStatus) => {
        try {
            const actualTab = activeTab === 'miscellaneous' ? miscTab : activeTab;
            if (actualTab === 'transactions') {
                await setDoc(doc(db, actualTab, item._id), { ...item, status: newStatus }, { merge: true });
                const vaultDataRef = doc(db, 'users', item.userId, 'transactions', 'vaultData');
                const matchesDataRef = doc(db, 'users', item.userId, 'transactions', 'matchesData');
                
                const vaultSnap = await getDoc(vaultDataRef);
                if (vaultSnap.exists()) {
                    const vData = vaultSnap.data();
                    if (item.type === 'WITHDRAWAL') {
                        let updatedHistory = vData.withdrawalHistory || [];
                        let found = false;
                        updatedHistory = updatedHistory.map(w => {
                            if (w.id === item._id) {
                                found = true;
                                return { ...w, status: newStatus };
                            }
                            return w;
                        });
                        
                        if (found) {
                            const vUpdates = { withdrawalHistory: updatedHistory };
                            
                            if (newStatus === 'APPROVED') {
                                // update bountyVault in matchesData
                                const matchesSnap = await getDoc(matchesDataRef);
                                if (matchesSnap.exists()) {
                                    const mData = matchesSnap.data();
                                    let bVault = mData.bountyVault || {};
                                    bVault.totalCashouts = (bVault.totalCashouts || 0) + parseInt(item.amount);
                                    await setDoc(matchesDataRef, { bountyVault: bVault }, { merge: true });
                                }
                            } else if (newStatus === 'REJECTED') {
                                const refundCoins = parseInt(item.amount) / 1.25;
                                vUpdates.eCoinsBalance = (vData.eCoinsBalance || 0) + refundCoins;
                                vUpdates.displayCoins = vUpdates.eCoinsBalance;
                            }
                            await setDoc(vaultDataRef, vUpdates, { merge: true });
                        }
                    }
                }
            } else {
                await setDoc(doc(db, actualTab, item._id), { ...item, status: newStatus }, { merge: true });
            }
        } catch (error) {
            console.error(error);
            alert("Error updating status!");
        }
    };

    const saveChanges = async () => {
        try {
            setSaveLoading(true);
            const parsed = editFormState;
            const actualTab = activeTab === 'miscellaneous' ? miscTab : activeTab;

            if (actualTab === 'findTeammates' || actualTab === 'bgmiAccounts' || actualTab === 'staffApplications' || actualTab === 'playerReports' || actualTab === 'paymentIssues') {
                await setDoc(doc(db, actualTab, editingItem._id), parsed, { merge: false });
            } else if (actualTab === 'matches' || actualTab === 'mobileMatches' || actualTab === 'emulatorMatches') {
                if (editingItem && editingItem._isSingleMatch) {
                    const newArray = [...matchesData];
                    newArray[editingItem._index] = parsed[0];
                    if (activeTab === 'matches') { await saveCombinedMatches(newArray); } else { await setDoc(doc(db, 'dailyMatches', activeTab, 'dates', selectedDate), { data: newArray }); }
                } else {
                    if (activeTab === 'matches') {
                        await saveCombinedMatches(parsed);
                    } else {
                        await setDoc(doc(db, 'dailyMatches', activeTab, 'dates', selectedDate), { data: parsed });
                    }
                }
                // Fix for ghost document in Firebase console:
                if (activeTab !== 'matches') {
                    await setDoc(doc(db, 'dailyMatches', activeTab), { exists: true }, { merge: true });
                }
            }
            setEditingItem(null);
        } catch (error) {
            console.error(error);
            alert("Error saving!");
        } finally {
            setSaveLoading(false);
        }
    };

    if (selectedMatchForSlots) {
        return (
            <AdminTournamentSlots
                tournament={selectedMatchForSlots}
                selectedDate={selectedDate}
                onClose={() => setSelectedMatchForSlots(null)}
            />
        );
    }

    return (
        <div className="min-h-screen p-[10px] w-full text-gray-900 dark:text-white relative font-['Monda'] bg-white dark:bg-[#0a0a0a]">
            {/* Theme Toggle */}
            <button 
                onClick={toggleTheme} 
                className="absolute top-[10px] right-[10px] p-2 rounded-full bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10 flex items-center justify-center transition-colors z-50"
            >
                {theme === 'dark' ? (
                    <svg className="w-5 h-5 text-yellow-500" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z" clipRule="evenodd" /></svg>
                ) : (
                    <svg className="w-5 h-5 text-gray-500" fill="currentColor" viewBox="0 0 20 20"><path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" /></svg>
                )}
            </button>

            {editingItem && editFormState ? (
                // Full Page Edit Form
                <div className="w-full flex flex-col">
                    <div className="flex justify-between items-center mb-8">
                        <div>
                            <h3 className="text-3xl font-black uppercase">
                                Edit {Array.isArray(editFormState) ? `Match ${editFormState.length > 0 && (editFormState[0].id || editingItem?._index + 1)}` : 'Data'}
                            </h3>
                            <p className="text-sm text-gray-500 font-bold uppercase mt-1">Make your changes below and save</p>
                        </div>
                        <div className="flex gap-4">
                            <button onClick={() => setEditingItem(null)} className="px-6 py-2 bg-gray-200 dark:bg-white/5 rounded-lg font-bold uppercase transition-colors">CANCEL</button>
                            <button onClick={saveChanges} disabled={saveLoading} className="px-6 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg font-bold uppercase transition-colors">
                                {saveLoading ? 'SAVING...' : 'SAVE CHANGES'}
                            </button>
                        </div>
                    </div>
                    
                    <div className="flex-1 w-full space-y-4">
                        {Array.isArray(editFormState) ? (
                            <div className="space-y-6">
                                {editFormState.map((match, index) => (
                                    <div 
                                        key={match.id + '-' + index} 
                                        className={`bg-gray-50 dark:bg-[#1a1a24] p-6 rounded-xl border border-gray-200 dark:border-white/10 shadow-sm relative transition-all ${draggedIndex === index ? 'opacity-50 border-purple-500/50' : ''}`}
                                    >
                                        <div className="flex justify-between items-center mb-6 pb-4 border-b border-gray-200 dark:border-white/10">
                                            <div className="flex items-center gap-3">
                                                <h4 className="font-bold text-lg uppercase">Match #{match.id || index + 1}</h4>
                                            </div>
                                            <div className="flex gap-2 items-center flex-wrap justify-end">
                                                


                                                {match.isCompleted ? (
                                                    match.livestreamLink && (
                                                        <a href={match.livestreamLink} target="_blank" rel="noopener noreferrer" className="px-4 py-2 bg-pink-500/10 text-pink-500 rounded hover:bg-pink-500 hover:text-white transition-colors text-xs font-bold uppercase border border-pink-500/20 flex items-center gap-1.5 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                                                            WATCH STREAM
                                                        </a>
                                                    )
                                                ) : !match.isLiveNow ? (
                                                    startingMatchIndex === index ? (
                                                        <div className="flex gap-2">
                                                            <input
                                                                type="text"
                                                                placeholder="LIVESTREAM URL"
                                                                value={livestreamUrlInput}
                                                                onChange={(e) => setLivestreamUrlInput(e.target.value)}
                                                                className="px-3 py-1.5 bg-white dark:bg-black/40 border border-gray-300 dark:border-white/10 rounded outline-none text-xs w-48 font-medium"
                                                                onClick={(e) => e.stopPropagation()}
                                                            />
                                                            <button type="button" onClick={(e) => {
                                                                e.stopPropagation();
                                                                if (livestreamUrlInput) {
                                                                    const newArray = [...editFormState];
                                                                    newArray[index] = { ...newArray[index], isLiveNow: true, livestreamLink: livestreamUrlInput };
                                                                    setEditFormState(newArray);
                                                                    setStartingMatchIndex(null);
                                                                    setLivestreamUrlInput('');
                                                                }
                                                            }} className="px-4 py-2 bg-green-500/10 text-green-500 rounded hover:bg-green-500 hover:text-white transition-colors text-xs font-bold uppercase border border-green-500/20 whitespace-nowrap">
                                                                CONFIRM START
                                                            </button>
                                                            <button type="button" onClick={(e) => {
                                                                e.stopPropagation();
                                                                setStartingMatchIndex(null);
                                                                setLivestreamUrlInput('');
                                                            }} className="px-4 py-2 bg-gray-500/10 text-gray-500 rounded hover:bg-gray-500 hover:text-white transition-colors text-xs font-bold uppercase border border-gray-500/20">
                                                                CANCEL
                                                            </button>
                                                        </div>
                                                    ) : (
                                                        <button type="button" onClick={(e) => {
                                                            e.stopPropagation();
                                                            setStartingMatchIndex(index);
                                                            setLivestreamUrlInput('');
                                                        }} 
                                                        disabled={false}
                                                        className={`px-4 py-2 rounded transition-colors text-xs font-bold uppercase border whitespace-nowrap bg-green-500/10 text-green-500 hover:bg-green-500 hover:text-white border-green-500/20`}
                                                        title={"Start the match"}>
                                                            START THE MATCH
                                                        </button>
                                                    )
                                                ) : (
                                                    <a href={match.livestreamLink} target="_blank" rel="noopener noreferrer" className="px-4 py-2 bg-pink-500/10 text-pink-500 rounded hover:bg-pink-500 hover:text-white transition-colors text-xs font-bold uppercase border border-pink-500/20 flex items-center gap-1.5 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                                                        <div className="w-1.5 h-1.5 rounded-full bg-pink-500 animate-pulse"></div>
                                                        WATCH LIVE
                                                    </a>
                                                )}


                                                <button 
                                                    type="button" 
                                                    onClick={(e) => { 
                                                        e.stopPropagation(); 
                                                        const newArray = [...editFormState];
                                                        newArray[index] = { ...newArray[index], isOnboardingPaused: !match.isOnboardingPaused };
                                                        setEditFormState(newArray);
                                                    }} 
                                                    disabled={match.isLiveNow || match.isCompleted}
                                                    className={`px-4 py-2 rounded transition-colors text-xs font-bold uppercase border whitespace-nowrap ${(match.isLiveNow || match.isCompleted) ? 'bg-gray-500/10 text-gray-500 border-gray-500/20 cursor-not-allowed opacity-50' : match.isOnboardingPaused ? 'bg-green-500/10 text-green-500 hover:bg-green-500 hover:text-white border-green-500/20' : 'bg-red-500/10 text-red-500 hover:bg-red-500 hover:text-white border-red-500/20'}`}
                                                >
                                                    {match.isOnboardingPaused ? 'Resume Onboarding' : 'Pause Onboarding'}
                                                </button>

                                                    <button 
                                                        onClick={() => handleConcludeMatch(editingItem?._index ?? index)}
                                                        disabled={!match.isLiveNow || match.isCompleted}
                                                        title={match.isCompleted ? "Match is concluded" : (!match.isLiveNow ? "Match must be live to conclude" : "")}
                                                        className={`px-4 py-2 rounded transition-colors text-xs font-bold uppercase whitespace-nowrap border ${(!match.isLiveNow || match.isCompleted) ? 'bg-gray-500/10 text-gray-500 border-gray-500/20 cursor-not-allowed opacity-50' : 'bg-red-500 hover:bg-red-600 text-white border-red-500'}`}
                                                    >
                                                        MATCH CONCLUDED
                                                    </button>
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
                                            {Object.entries(match).filter(([key]) => !['bookedSlots', 'roomId', 'roomPassword', 'isCompleted', 'isReadyCheckActive'].includes(key)).sort(([keyA], [keyB]) => {
                                                const fieldOrder = ['id', 'title', 'lobbyType', 'map', 'mode', 'entry', 'prize', 'killReward', 'slots', 'time'];
                                                const indexA = fieldOrder.indexOf(keyA);
                                                const indexB = fieldOrder.indexOf(keyB);
                                                if (indexA === -1 && indexB === -1) return keyA.localeCompare(keyB);
                                                if (indexA === -1) return 1;
                                                if (indexB === -1) return -1;
                                                return indexA - indexB;
                                            }).map(([key, val]) => (
                                                <div key={key} className="flex flex-col gap-2">
                                                    <label className="text-xs font-bold uppercase text-gray-500 tracking-wider">
                                                        {key === 'entry' ? 'PARTICIPATION FEE (₹)' : key === 'prize' ? 'PRIZE POOL' : key === 'killReward' ? 'PER KILL PRICE' : key}
                                                    </label>
                                                    {key === 'lobbyType' ? (
                                                        <select
                                                            value={val || 'CLEAN LOBBY'}
                                                            onChange={(e) => handleFieldChange(key, e.target.value, index)}
                                                            className="bg-white dark:bg-black/40 border border-gray-300 dark:border-white/10 rounded-lg p-3 outline-none font-medium uppercase appearance-none"
                                                        >
                                                            <option value="CLEAN LOBBY">CLEAN LOBBY</option>
                                                            <option value="HACKER vs HACKER">HACKER vs HACKER</option>
                                                        </select>
                                                    ) : key === 'map' ? (
                                                        <select
                                                            value={val || 'Erangel'}
                                                            onChange={(e) => {
                                                                const newMap = e.target.value;
                                                                let totalSlots = 100;
                                                                if (newMap === 'Livik') totalSlots = 52;
                                                                else if (newMap === 'TDM 4v4') totalSlots = 8;
                                                                else if (newMap === 'TDM 8v8') totalSlots = 16;
                                                                
                                                                const newArray = [...editFormState];
                                                                newArray[index] = { 
                                                                    ...newArray[index], 
                                                                    map: newMap,
                                                                    slots: `0 / ${totalSlots}`
                                                                };
                                                                setEditFormState(newArray);
                                                            }}
                                                            className="bg-white dark:bg-black/40 border border-gray-300 dark:border-white/10 rounded-lg p-3 outline-none font-medium uppercase appearance-none"
                                                        >
                                                            <option value="Livik">Livik</option>
                                                            <option value="TDM 4v4">TDM 4v4</option>
                                                            <option value="TDM 8v8">TDM 8v8</option>
                                                            <option value="Sanhok">Sanhok</option>
                                                            <option value="Erangel">Erangel</option>
                                                            <option value="Miramar">Miramar</option>
                                                        </select>
                                                    ) : key === 'mode' ? (
                                                        <select
                                                            value={val || 'Squad'}
                                                            onChange={(e) => handleFieldChange(key, e.target.value, index)}
                                                            className="bg-white dark:bg-black/40 border border-gray-300 dark:border-white/10 rounded-lg p-3 outline-none font-medium uppercase appearance-none"
                                                        >
                                                            <option value="Duo">Duo</option>
                                                            <option value="Solo">Solo</option>
                                                            <option value="Squad">Squad</option>
                                                        </select>
                                                    ) : key === 'time' ? (
                                                        <select
                                                            value={val || '12:00 PM'}
                                                            onChange={(e) => handleFieldChange(key, e.target.value, index)}
                                                            className="bg-white dark:bg-black/40 border border-gray-300 dark:border-white/10 rounded-lg p-3 outline-none font-medium custom-scrollbar uppercase appearance-none"
                                                        >
                                                            {Array.from({ length: 48 }).map((_, i) => {
                                                                let h = Math.floor(i / 2);
                                                                let m = (i % 2 === 0) ? '00' : '30';
                                                                let ampm = h >= 12 ? 'PM' : 'AM';
                                                                let displayH = h % 12;
                                                                if (displayH === 0) displayH = 12;
                                                                let timeStr = `${displayH.toString().padStart(2, '0')}:${m} ${ampm}`;
                                                                return <option key={timeStr} value={timeStr}>{timeStr}</option>
                                                            })}
                                                        </select>
                                                    ) : typeof val === 'boolean' ? (
                                                        <select
                                                            value={val.toString()}
                                                            onChange={(e) => handleFieldChange(key, e.target.value === 'true', index)}
                                                            className="bg-white dark:bg-black/40 border border-gray-300 dark:border-white/10 rounded-lg p-3 outline-none font-medium uppercase appearance-none"
                                                        >
                                                            <option value="true">True</option>
                                                            <option value="false">False</option>
                                                        </select>
                                                    ) : (key === 'slots' || key === 'prize' || key === 'killReward' || key === 'id') ? (
                                                        <input
                                                            type="text"
                                                            value={key === 'slots' ? (val ? val.toString().split(' / ').pop().trim() : '') : key === 'id' ? val : 'DYNAMIC'}
                                                            readOnly
                                                            className="bg-gray-100 dark:bg-black/20 border border-gray-300 dark:border-white/10 rounded-lg p-3 outline-none font-medium pointer-events-none text-center font-bold uppercase"
                                                        />
                                                    ) : key === 'entry' ? (
                                                        <div className="flex flex-col gap-1">
                                                            <input
                                                                type="text"
                                                                value={parseInt((val || "0").replace(/\D/g, '')) * 1.25 || ''}
                                                                onChange={(e) => {
                                                                    const rs = parseInt(e.target.value.replace(/\D/g, '')) || 0;
                                                                    const glpc = Math.floor(rs / 1.25);
                                                                    handleFieldChange(key, `${glpc} GLPC`, index);
                                                                }}
                                                                onKeyDown={(e) => {
                                                                    if (!/[0-9]/.test(e.key) && e.key !== 'Backspace' && e.key !== 'Delete' && e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') {
                                                                        e.preventDefault();
                                                                    }
                                                                }}
                                                                placeholder="₹"
                                                                className="bg-white dark:bg-black/40 border border-gray-300 dark:border-white/10 rounded-lg p-3 outline-none font-medium uppercase"
                                                            />
                                                            <div className="text-[10px] text-pink-500 font-bold ml-1 uppercase tracking-wider">
                                                                CONVERTS TO: {val || '0 GLPC'}
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <input
                                                            type="text"
                                                            value={val || ''}
                                                            onChange={(e) => {
                                                                const parsedVal = typeof val === 'number' ? Number(e.target.value) : e.target.value;
                                                                handleFieldChange(key, parsedVal, index);
                                                            }}
                                                            className="bg-white dark:bg-black/40 border border-gray-300 dark:border-white/10 rounded-lg p-3 outline-none font-medium uppercase"
                                                        />
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="bg-gray-50 dark:bg-[#1a1a24] p-6 rounded-xl border border-gray-200 dark:border-white/10 shadow-sm grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 w-full">
                                {Object.entries(editFormState).sort(([keyA], [keyB]) => {
                                    const fieldOrder = ['id', 'title', 'lobbyType', 'map', 'mode', 'entry', 'prize', 'killReward', 'slots', 'time'];
                                    const indexA = fieldOrder.indexOf(keyA);
                                    const indexB = fieldOrder.indexOf(keyB);
                                    if (indexA === -1 && indexB === -1) return keyA.localeCompare(keyB);
                                    if (indexA === -1) return 1;
                                    if (indexB === -1) return -1;
                                    return indexA - indexB;
                                }).map(([key, val]) => (
                                    <div key={key} className="flex flex-col gap-2">
                                        <label className="text-xs font-bold uppercase text-gray-500 tracking-wider">
                                            {key === 'entry' ? 'PARTICIPATION FEE (₹)' : key === 'prize' ? 'PRIZE POOL' : key === 'killReward' ? 'PER KILL PRICE' : key}
                                        </label>
                                        {key === 'lobbyType' ? (
                                            <select
                                                value={val || 'CLEAN LOBBY'}
                                                onChange={(e) => handleFieldChange(key, e.target.value)}
                                                className="bg-white dark:bg-black/40 border border-gray-300 dark:border-white/10 rounded-lg p-3 outline-none font-medium uppercase appearance-none"
                                            >
                                                <option value="CLEAN LOBBY">CLEAN LOBBY</option>
                                                <option value="HACKER vs HACKER">HACKER vs HACKER</option>
                                            </select>
                                        ) : key === 'map' ? (
                                            <select
                                                value={val || 'Erangel'}
                                                onChange={(e) => {
                                                    const newMap = e.target.value;
                                                    let totalSlots = 100;
                                                    if (newMap === 'Livik') totalSlots = 52;
                                                    else if (newMap === 'TDM 4v4') totalSlots = 8;
                                                    else if (newMap === 'TDM 8v8') totalSlots = 16;
                                                    
                                                    setEditFormState({ 
                                                        ...editFormState, 
                                                        map: newMap,
                                                        slots: `0 / ${totalSlots}`
                                                    });
                                                }}
                                                className="bg-white dark:bg-black/40 border border-gray-300 dark:border-white/10 rounded-lg p-3 outline-none font-medium uppercase appearance-none"
                                            >
                                                <option value="Livik">Livik</option>
                                                <option value="TDM 4v4">TDM 4v4</option>
                                                <option value="TDM 8v8">TDM 8v8</option>
                                                <option value="Sanhok">Sanhok</option>
                                                <option value="Erangel">Erangel</option>
                                                <option value="Miramar">Miramar</option>
                                            </select>
                                        ) : key === 'mode' ? (
                                            <select
                                                value={val || 'Squad'}
                                                onChange={(e) => handleFieldChange(key, e.target.value)}
                                                className="bg-white dark:bg-black/40 border border-gray-300 dark:border-white/10 rounded-lg p-3 outline-none font-medium uppercase appearance-none"
                                            >
                                                <option value="Duo">Duo</option>
                                                <option value="Solo">Solo</option>
                                                <option value="Squad">Squad</option>
                                            </select>
                                        ) : key === 'time' ? (
                                            <select
                                                value={val || '12:00 PM'}
                                                onChange={(e) => handleFieldChange(key, e.target.value)}
                                                className="bg-white dark:bg-black/40 border border-gray-300 dark:border-white/10 rounded-lg p-3 outline-none font-medium custom-scrollbar uppercase appearance-none"
                                            >
                                                {Array.from({ length: 48 }).map((_, i) => {
                                                    let h = Math.floor(i / 2);
                                                    let m = (i % 2 === 0) ? '00' : '30';
                                                    let ampm = h >= 12 ? 'PM' : 'AM';
                                                    let displayH = h % 12;
                                                    if (displayH === 0) displayH = 12;
                                                    let timeStr = `${displayH.toString().padStart(2, '0')}:${m} ${ampm}`;
                                                    return <option key={timeStr} value={timeStr}>{timeStr}</option>
                                                })}
                                            </select>
                                        ) : typeof val === 'boolean' ? (
                                            <select
                                                value={val.toString()}
                                                onChange={(e) => handleFieldChange(key, e.target.value === 'true')}
                                                className="bg-white dark:bg-black/40 border border-gray-300 dark:border-white/10 rounded-lg p-3 outline-none font-medium uppercase appearance-none"
                                            >
                                                <option value="true">True</option>
                                                <option value="false">False</option>
                                            </select>
                                        ) : (key === 'slots' || key === 'prize' || key === 'killReward' || key === 'id') ? (
                                            <input
                                                type="text"
                                                value={key === 'slots' ? (val ? val.toString().split(' / ').pop().trim() : '') : key === 'id' ? val : 'DYNAMIC'}
                                                readOnly
                                                className="bg-gray-100 dark:bg-black/20 border border-gray-300 dark:border-white/10 rounded-lg p-3 outline-none font-medium pointer-events-none text-center font-bold uppercase"
                                            />
                                        ) : key === 'entry' ? (
                                            <div className="flex flex-col gap-1">
                                                <input
                                                    type="text"
                                                    value={parseInt((val || "0").replace(/\D/g, '')) * 1.25 || ''}
                                                    onChange={(e) => {
                                                        const rs = parseInt(e.target.value.replace(/\D/g, '')) || 0;
                                                        const glpc = Math.floor(rs / 1.25);
                                                        handleFieldChange(key, `${glpc} GLPC`);
                                                    }}
                                                    onKeyDown={(e) => {
                                                        if (!/[0-9]/.test(e.key) && e.key !== 'Backspace' && e.key !== 'Delete' && e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') {
                                                            e.preventDefault();
                                                        }
                                                    }}
                                                    placeholder="₹"
                                                    className="bg-white dark:bg-black/40 border border-gray-300 dark:border-white/10 rounded-lg p-3 outline-none font-medium uppercase"
                                                />
                                                <div className="text-[10px] text-pink-500 font-bold ml-1 uppercase tracking-wider">
                                                    CONVERTS TO: {val || '0 GLPC'}
                                                </div>
                                            </div>
                                        ) : Array.isArray(val) ? (
                                            <input
                                                type="text"
                                                value={val.join(', ')}
                                                onChange={(e) => handleFieldChange(key, e.target.value.split(',').map(s => s.trim()))}
                                                className="bg-white dark:bg-black/40 border border-gray-300 dark:border-white/10 rounded-lg p-3 outline-none font-medium uppercase"
                                                placeholder="Comma separated values"
                                            />
                                        ) : typeof val === 'object' && val !== null ? (
                                            <input
                                                type="text"
                                                value={JSON.stringify(val)}
                                                readOnly
                                                className="bg-gray-100 dark:bg-black/20 border border-gray-300 dark:border-white/10 rounded-lg p-3 outline-none font-medium cursor-not-allowed opacity-70"
                                            />
                                        ) : (
                                            <input
                                                type="text"
                                                value={val || ''}
                                                onChange={(e) => {
                                                    const parsedVal = typeof val === 'number' ? Number(e.target.value) : e.target.value;
                                                    handleFieldChange(key, parsedVal);
                                                }}
                                                className="bg-white dark:bg-black/40 border border-gray-300 dark:border-white/10 rounded-lg p-3 outline-none font-medium uppercase"
                                            />
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            ) : (
                // Table View (List)
                <>
                    <div className="flex gap-4 mb-8 overflow-x-auto pb-2 custom-scrollbar">
                        {[
                            { id: 'findTeammates', label: 'Teammates' },
                            { id: 'bgmiAccounts', label: 'BGMI Accounts' },
                            { id: 'matches', label: 'Matches' },
                            { id: 'hallOfFame', label: 'Hall of Fame' },
                            { id: 'miscellaneous', label: 'Miscellaneous' }
                        ].map(tab => (
                            <button 
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`px-6 py-3 rounded-lg font-bold uppercase whitespace-nowrap transition-colors ${activeTab === tab.id ? 'bg-pink-500 text-white' : 'bg-gray-100 dark:bg-white/5 hover:bg-gray-200 dark:hover:bg-white/10'}`}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>

                    {(activeTab === 'matches') && (
                        <div className="mb-6 flex flex-col md:flex-row md:items-center gap-4">
                            <div className="flex gap-2 overflow-x-auto custom-scrollbar pb-2">
                                {Array.from({ length: 7 }).map((_, i) => {
                                    const d = new Date();
                                    d.setDate(d.getDate() - 3 + i);
                                    const year = d.getFullYear();
                                    const month = String(d.getMonth() + 1).padStart(2, '0');
                                    const day = String(d.getDate()).padStart(2, '0');
                                    const dateStr = `${year}-${month}-${day}`;
                                    const displayStr = d.toLocaleDateString('en-US', { day: '2-digit', month: 'short' });
                                    const label = i === 3 ? 'TODAY' : displayStr;
                                    return (
                                        <button 
                                            key={dateStr}
                                            onClick={() => setSelectedDate(dateStr)}
                                            className={`px-4 py-3 rounded-lg font-bold uppercase whitespace-nowrap transition-colors border ${selectedDate === dateStr ? 'bg-pink-500 text-white border-pink-500' : 'bg-gray-100 dark:bg-white/5 hover:bg-gray-200 dark:hover:bg-white/10 border-gray-300 dark:border-white/10'}`}
                                        >
                                            {label}
                                        </button>
                                    );
                                })}
                            </div>
                            <div className="md:ml-auto flex flex-col sm:flex-row items-center gap-2">
                                <select 
                                    id="newMatchPlatform" 
                                    className="px-4 py-3 bg-gray-100 dark:bg-white/5 border border-gray-300 dark:border-white/10 rounded-lg font-bold uppercase text-sm cursor-pointer outline-none"
                                >
                                    <option value="MOBILE">MOBILE LOBBY</option>
                                    <option value="EMULATOR">EMULATOR LOBBY</option>
                                </select>
                                <button 
                                    onClick={() => {
                                        const platform = document.getElementById('newMatchPlatform').value;
                                        handleAddMatch(platform);
                                    }}
                                    className="px-6 py-3 bg-purple-500 hover:bg-purple-600 text-white rounded-lg font-bold uppercase transition-colors whitespace-nowrap"
                                >
                                    + ADD NEW MATCH
                                </button>
                            </div>
                        </div>
                    )}

                    {activeTab === 'miscellaneous' && (
                        <div className="flex gap-4 mb-8 overflow-x-auto pb-2 custom-scrollbar border-b border-gray-200 dark:border-white/10">
                            {[
                                { id: 'staffApplications', label: 'Staff Applications' },
                                { id: 'playerReports', label: 'Player Reports' },
                                { id: 'paymentIssues', label: 'Payment Issues' },
                                { id: 'transactions', label: 'Transactions' }
                            ].map(tab => (
                                <button 
                                    key={tab.id}
                                    onClick={() => setMiscTab(tab.id)}
                                    className={`px-4 py-2 font-bold uppercase whitespace-nowrap transition-colors border-b-2 ${miscTab === tab.id ? 'border-pink-500 text-pink-500' : 'border-transparent text-gray-500 hover:text-gray-800 dark:hover:text-gray-300'}`}
                                >
                                    {tab.label}
                                </button>
                            ))}
                        </div>
                    )}

                    <div className={activeTab === 'miscellaneous' && miscTab === 'transactions' ? "w-full" : "bg-gray-50 dark:bg-[#1a1a24] rounded-xl border border-gray-200 dark:border-white/10 p-4 w-full"}>
                        {loading ? (
                            <div className="text-center py-10 font-bold uppercase tracking-wider text-gray-500">Loading Database...</div>
                        ) : (
                            <>
                                {activeTab === 'findTeammates' && (
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 min-[1400px]:grid-cols-4 gap-[15px]">
                                        {data.map((gamer, index) => (
                                            <GamerCard 
                                                key={gamer._id} 
                                                gamer={{id: gamer._id, ...gamer}} 
                                                index={index} 
                                                isAdmin={true} 
                                                onDeleteAdmin={() => handleDelete(gamer)}
                                            />
                                        ))}
                                        {data.length === 0 && <div className="col-span-full text-center py-12 text-gray-500 font-bold uppercase">No players found looking for team.</div>}
                                    </div>
                                )}
                                {activeTab === 'bgmiAccounts' && (
                                    <div className="overflow-x-auto w-full">
                                        <table className="w-full text-left min-w-[600px]">
                                            <thead className="border-b border-gray-200 dark:border-white/10 text-xs font-bold uppercase text-gray-500 tracking-wider">
                                                <tr>
                                                    <th className="p-4">ID</th>
                                                    <th className="p-4">Name / Title</th>
                                                    <th className="p-4 text-right">Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {data.map(item => (
                                                    <tr key={item._id} className="border-b border-gray-200 dark:border-white/5 hover:bg-gray-100 dark:hover:bg-white/5 transition-colors">
                                                        <td className="p-4 font-mono text-xs">{item._id}</td>
                                                        <td className="p-4 font-bold">{item.name || item.title || 'No Title'}</td>
                                                        <td className="p-4 text-right flex justify-end gap-3">
                                                            <button onClick={() => handleEdit(item)} className="p-2 bg-blue-500/10 text-blue-500 rounded hover:bg-blue-500 hover:text-white transition-colors border border-blue-500/20" title="Edit">
                                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path></svg>
                                                            </button>
                                                            <button onClick={() => handleDelete(item)} className="p-2 bg-red-500/10 text-red-500 rounded hover:bg-red-500 hover:text-white transition-colors border border-red-500/20" title="Delete">
                                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path></svg>
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                        {data.length === 0 && <div className="text-center py-12 text-gray-500 font-bold uppercase">No data found in this collection.</div>}
                                    </div>
                                )}

                                {activeTab === 'miscellaneous' && (
                                    <div className="flex flex-col gap-[10px] w-full">
                                        {miscTab === 'transactions' && (
                                            <div className="flex flex-col md:flex-row gap-[10px] mb-[10px]">
                                                <input 
                                                    type="text" 
                                                    placeholder="Search Transaction ID..." 
                                                    value={txnSearch}
                                                    onChange={(e) => setTxnSearch(e.target.value)}
                                                    className="flex-1 bg-white dark:bg-black/40 border border-gray-200 dark:border-white/10 rounded-lg px-4 py-3 outline-none font-mono text-sm"
                                                />
                                                <select 
                                                    value={txnFilter} 
                                                    onChange={(e) => setTxnFilter(e.target.value)}
                                                    className="bg-white dark:bg-black/40 border border-gray-200 dark:border-white/10 rounded-lg px-4 py-3 outline-none font-bold uppercase text-sm"
                                                >
                                                    <option value="ALL">ALL TRANSACTIONS</option>
                                                    <option value="TOPUP">TOP UPS ONLY</option>
                                                    <option value="WITHDRAWAL">WITHDRAWALS ONLY</option>
                                                </select>
                                            </div>
                                        )}
                                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-[10px]">
                                            {data.filter(item => {
                                                if (miscTab !== 'transactions') return true;
                                                const matchesSearch = item._id.toLowerCase().includes(txnSearch.toLowerCase());
                                                const matchesFilter = txnFilter === 'ALL' || item.type === txnFilter;
                                                return matchesSearch && matchesFilter;
                                            }).map(item => (
                                                <div key={item._id} className="bg-white dark:bg-[#1a1a24] border border-gray-200 dark:border-white/10 rounded-none overflow-hidden shadow-sm hover:shadow-md transition-all flex flex-col justify-between relative font-['Monda']">
                                                    
                                                    {/* Edge to Edge Tag for Transactions */}
                                                    {miscTab === 'transactions' && (
                                                        <div className={`absolute top-0 right-0 px-4 py-1.5 text-[10px] font-black uppercase tracking-widest rounded-none ${item.status === 'APPROVED' ? 'bg-green-500 text-white' : item.status === 'COMPLETED' ? 'bg-blue-500 text-white' : item.status === 'REJECTED' ? 'bg-red-500 text-white' : 'bg-yellow-500 text-white'}`}>
                                                            {item.status || 'PENDING'}
                                                        </div>
                                                    )}

                                                    <div className="p-[15px] flex flex-col gap-3 mt-2">
                                                        <div className="flex justify-between items-center mb-2 border-b border-gray-100 dark:border-white/5 pb-3">
                                                            <div className="flex flex-col gap-0.5">
                                                                <span className="text-[12px] font-black uppercase text-gray-800 dark:text-gray-200 tracking-widest">{miscTab === 'transactions' ? item._id : item._id.substring(0, 8) + '...'}</span>
                                                                {item.timestamp?.seconds && (
                                                                    <span className="text-[9px] font-bold text-gray-400 uppercase tracking-widest">
                                                                        {new Date(item.timestamp.seconds * 1000).toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' })} • {new Date(item.timestamp.seconds * 1000).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                                                                    </span>
                                                                )}
                                                            </div>
                                                            {miscTab !== 'transactions' && (
                                                                <span className={`text-[10px] font-black uppercase px-2 py-1 rounded-none tracking-widest ${item.status === 'APPROVED' ? 'bg-green-500/10 text-green-500' : item.status === 'REJECTED' ? 'bg-red-500/10 text-red-500' : 'bg-yellow-500/10 text-yellow-500'}`}>
                                                                    {item.status || 'PENDING'}
                                                                </span>
                                                            )}
                                                        </div>
                                                        <div className="grid grid-cols-2 gap-4">
                                                            {Object.entries(item).map(([key, val]) => {
                                                                if (key === '_id' || key === 'status' || key === 'timestamp' || key === 'experience' || key === 'reason' || key === 'txDetails' || key === 'userName') return null;
                                                                
                                                                // If the tab is transactions and key is userId, display userName if available
                                                                let displayKey = key;
                                                                let displayVal = val;
                                                                if (miscTab === 'transactions' && key === 'userId') {
                                                                    displayKey = 'USER NAME';
                                                                    displayVal = item.userName || val;
                                                                }

                                                                return (
                                                                    <div key={key} className="flex flex-col bg-gray-50 dark:bg-white/5 p-3 rounded-none border border-gray-100 dark:border-white/5 overflow-hidden">
                                                                        <span className="text-[10px] font-black text-gray-500 uppercase tracking-widest mb-0.5">{displayKey}</span>
                                                                        <span className="text-sm font-black uppercase text-gray-900 dark:text-white truncate tracking-wide" title={String(displayVal)}>
                                                                            {typeof displayVal === 'object' ? JSON.stringify(displayVal) : String(displayVal)}
                                                                        </span>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                        
                                                        {/* Wide Section for long text */}
                                                        {(item.experience || item.reason || item.txDetails) && (
                                                            <div className="mt-1 flex flex-col bg-gray-50 dark:bg-white/5 p-3 rounded-none border border-gray-100 dark:border-white/5">
                                                                <span className="text-[10px] font-black text-gray-500 uppercase tracking-widest mb-1">
                                                                    {item.experience ? 'Experience' : item.reason ? 'Reason' : 'Details'}
                                                                </span>
                                                                <span className="text-sm font-black uppercase tracking-wide text-gray-800 dark:text-gray-300 whitespace-pre-wrap">
                                                                    {item.experience || item.reason || item.txDetails}
                                                                </span>
                                                            </div>
                                                        )}
                                                    </div>
                                                    {(!item.type || item.type !== 'TOPUP') && (
                                                        <div className="grid grid-cols-2 border-t border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/5">
                                                            <button onClick={() => handleStatusUpdate(item, 'APPROVED')} className="py-3 text-xs font-black uppercase tracking-widest text-green-500 hover:bg-green-500/10 transition-colors border-r border-gray-200 dark:border-white/10 flex justify-center items-center gap-2 rounded-none">
                                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg>
                                                                Approve
                                                            </button>
                                                            <button onClick={() => handleStatusUpdate(item, 'REJECTED')} className="py-3 text-xs font-black uppercase tracking-widest text-red-500 hover:bg-red-500/10 transition-colors flex justify-center items-center gap-2 rounded-none">
                                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
                                                                Reject
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                            {data.filter(item => {
                                                if (miscTab !== 'transactions') return true;
                                                const matchesSearch = item._id.toLowerCase().includes(txnSearch.toLowerCase());
                                                const matchesFilter = txnFilter === 'ALL' || item.type === txnFilter;
                                                return matchesSearch && matchesFilter;
                                            }).length === 0 && (
                                                <div className="col-span-full text-center py-12 text-gray-500 font-bold uppercase tracking-wider">
                                                    No records found
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {activeTab === 'hallOfFame' && (
                                    <div className="flex flex-col gap-8 w-full">
                                        <div className="w-full overflow-x-auto">
                                            <div className="flex justify-between items-center mb-6">
                                                <h3 className="text-xl font-bold uppercase tracking-wider">All Gamers ({allGamersList.length})</h3>
                                                {isFetchingAllGamers && (
                                                    <div className="text-sm font-bold text-pink-500 uppercase tracking-widest animate-pulse">Loading Gamers Data...</div>
                                                )}
                                            </div>
                                            
                                            <table className="w-full text-left min-w-[800px]">
                                                <thead>
                                                    <tr className="border-b border-gray-200 dark:border-white/10">
                                                        <th className="p-4 font-black uppercase tracking-wider text-xs text-gray-500">Player</th>
                                                        <th className="p-4 font-black uppercase tracking-wider text-xs text-gray-500 text-center">Matches</th>
                                                        <th className="p-4 font-black uppercase tracking-wider text-xs text-gray-500 text-center">Total XP</th>
                                                        <th className="p-4 font-black uppercase tracking-wider text-xs text-gray-500 text-center">Earnings</th>
                                                        <th className="p-4 font-black uppercase tracking-wider text-xs text-gray-500 text-right">Actions</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {allGamersList.map(player => {
                                                        const isInHoF = hallOfFameData.some(p => p._id === player._id);
                                                        const isInHoS = hallOfShameData.some(p => p._id === player._id);
                                                        return (
                                                            <tr key={player._id} className={`border-b border-gray-100 dark:border-white/5 hover:bg-gray-50 dark:hover:bg-white/5 transition-colors ${isInHoF ? 'bg-pink-500/5 dark:bg-pink-500/10' : isInHoS ? 'bg-red-500/5 dark:bg-red-500/10' : ''}`}>
                                                                <td className="p-4 font-bold">
                                                                    <div className="flex flex-col">
                                                                        <span>{player.name}</span>
                                                                        <span className="text-[10px] text-gray-400 font-normal mt-1 flex flex-col gap-0.5">
                                                                            {player.gameUid1 && <span>{player.gameUid1} {player.gameIgn1 ? `(${player.gameIgn1})` : ''}</span>}
                                                                            {player.gameUid2 && <span>{player.gameUid2} {player.gameIgn2 ? `(${player.gameIgn2})` : ''}</span>}
                                                                            {!player.gameUid1 && !player.gameUid2 && <span>N/A</span>}
                                                                        </span>
                                                                    </div>
                                                                </td>
                                                                <td className="p-4 font-bold text-center opacity-70">{player.matchesPlayed}</td>
                                                                <td className="p-4 font-black text-center text-orange-500">{player.xp.toLocaleString()}</td>
                                                                <td className="p-4 font-black text-center text-pink-500">₹ {player.earnings.toLocaleString()}</td>
                                                                <td className="p-4 text-right">
                                                                    <div className="flex justify-end gap-2">
                                                                        {isInHoS ? (
                                                                            <button 
                                                                                onClick={async () => {
                                                                                    await deleteDoc(doc(db, 'shameboard', player._id));
                                                                                }}
                                                                                className="px-4 py-2 bg-red-500/10 text-red-500 hover:bg-red-500 hover:text-white rounded font-bold text-xs uppercase transition-colors"
                                                                            >
                                                                                Remove HOS
                                                                            </button>
                                                                        ) : (
                                                                            <button 
                                                                                onClick={async () => {
                                                                                    await setDoc(doc(db, 'shameboard', player._id), {
                                                                                        ...player,
                                                                                        lastUpdated: Date.now()
                                                                                    });
                                                                                }}
                                                                                className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded font-bold text-xs uppercase transition-colors"
                                                                            >
                                                                                SET AS HOS
                                                                            </button>
                                                                        )}
                                                                        {isInHoF ? (
                                                                            <button 
                                                                                onClick={async () => {
                                                                                    await deleteDoc(doc(db, 'leaderboard', player._id));
                                                                                }}
                                                                                className="px-4 py-2 bg-red-500/10 text-red-500 hover:bg-red-500 hover:text-white rounded font-bold text-xs uppercase transition-colors"
                                                                            >
                                                                                Remove
                                                                            </button>
                                                                        ) : (
                                                                            <button 
                                                                                onClick={async () => {
                                                                                    await setDoc(doc(db, 'leaderboard', player._id), {
                                                                                        ...player,
                                                                                        lastUpdated: Date.now()
                                                                                    });
                                                                                }}
                                                                                className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded font-bold text-xs uppercase transition-colors"
                                                                            >
                                                                                SET AS HOF
                                                                            </button>
                                                                        )}
                                                                        <button 
                                                                            onClick={async () => {
                                                                                try {
                                                                                    const userSnap = await getDoc(doc(db, 'users', player._id));
                                                                                    const userData = userSnap.data() || {};
                                                                                    setUserToEdit({ ...player, ...userData });
                                                                                    setEditUserData({
                                                                                        name: userData.profileData?.name || userData.name || '',
                                                                                        mobile: userData.profileData?.mobile || userData.mobile || '',
                                                                                        upiId: userData.profileData?.upiId || userData.upiId || '',
                                                                                        gameUid1: userData.profileData?.gameUid1 || userData.gameUid1 || '',
                                                                                        gameIgn1: userData.profileData?.gameIgn1 || userData.gameIgn1 || '',
                                                                                        gameUid2: userData.profileData?.gameUid2 || userData.gameUid2 || '',
                                                                                        gameIgn2: userData.profileData?.gameIgn2 || userData.gameIgn2 || ''
                                                                                    });
                                                                                    setEditUserModalOpen(true);
                                                                                } catch (err) {
                                                                                    console.error("Error fetching user data", err);
                                                                                    alert("Failed to fetch user data for editing.");
                                                                                }
                                                                            }}
                                                                            className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded font-bold text-xs uppercase transition-colors"
                                                                        >
                                                                            EDIT
                                                                        </button>
                                                                        {player.isBanned ? (
                                                                            <button 
                                                                                onClick={async () => {
                                                                                    console.log("Unban clicked for", player.name);
                                                                                    try {
                                                                                        await updateDoc(doc(db, 'users', player._id), {
                                                                                            isBanned: false,
                                                                                            banReason: null,
                                                                                            'profileData.trustScore': player.preBanTrustScore ?? 100
                                                                                        });
                                                                                        
                                                                                        setAllGamersList(prev => prev.map(p => 
                                                                                            p._id === player._id 
                                                                                                ? { ...p, isBanned: false, trustScore: p.preBanTrustScore ?? 100 } 
                                                                                                : p
                                                                                        ));
                                                                                        alert(`${player.name} has been unbanned.`);
                                                                                    } catch (error) {
                                                                                        console.error("Error unbanning user:", error);
                                                                                        alert("Error unbanning: " + error.message);
                                                                                    }
                                                                                }}
                                                                                className="px-4 py-2 bg-yellow-500 hover:bg-yellow-600 text-white rounded font-bold text-xs uppercase transition-colors"
                                                                            >
                                                                                UNBAN
                                                                            </button>
                                                                        ) : (
                                                                            <button 
                                                                                onClick={() => {
                                                                                    setUserToBan(player);
                                                                                    setBanReason("");
                                                                                    setBanModalOpen(true);
                                                                                }}
                                                                                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded font-bold text-xs uppercase transition-colors"
                                                                            >
                                                                                BAN
                                                                            </button>
                                                                        )}
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}

                                {(activeTab === 'matches') && (
                                    <div className="overflow-x-auto w-full">
                                        {!matchesData ? (
                                            <div className="text-center py-12 text-gray-500 font-bold uppercase leading-relaxed">
                                                No matches generated for this date yet.<br/>
                                                <span className="text-xs">Matches are automatically generated when visiting the homepage.</span>
                                            </div>
                                        ) : (
                                            <table className="w-full text-left min-w-[600px]">
                                                <thead className="border-b border-gray-200 dark:border-white/10 text-xs font-bold uppercase text-gray-500 tracking-wider">
                                                    <tr>
                                                        <th className="p-4">Match ID</th>
                                                        <th className="p-4">Title</th>
                                                        <th className="p-4">Time</th>
                                                        <th className="p-4">Slots</th>
                                                        <th className="p-4">Status</th>
                                                        <th className="p-4">Lobby</th>
                                                        <th className="p-4">Platform</th>
                                                        <th className="p-4 text-right">Actions</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {matchesData.map((match, i) => {
                                                        const filledSlots = parseInt((match.slots || "0 / 0").split(' / ')[0]) || 0;
                                                        const totalSlots = parseInt((match.slots || "0 / 0").split(' / ')[1]) || 100;
                                                        const isOngoing = !match.isCompleted && filledSlots >= totalSlots;
                                                        
                                                        return (
                                                        <tr key={i} className="border-b border-gray-200 dark:border-white/5 hover:bg-gray-100 dark:hover:bg-white/5 transition-colors">
                                                            <td className="p-4 font-bold">{match.id}</td>
                                                            <td className="p-4 font-bold text-purple-500">{match.title}</td>
                                                            <td className="p-4 font-medium">{match.time}</td>
                                                            <td className="p-4 font-mono">{match.slots}</td>
                                                            <td className="p-4">
                                                                {match.isCompleted ? (
                                                                    <span className="px-2 py-1 bg-gray-500/20 text-gray-500 text-xs font-bold rounded">CONCLUDED</span>
                                                                ) : isOngoing ? (
                                                                    <span className="px-2 py-1 bg-red-500/20 text-red-500 text-xs font-bold rounded flex items-center gap-1 w-fit">
                                                                        <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" /> LIVE
                                                                    </span>
                                                                ) : (
                                                                    <span className="px-2 py-1 bg-green-500/20 text-green-500 text-xs font-bold rounded">UPCOMING</span>
                                                                )}
                                                            </td>
                                                            <td className="p-4">
                                                                <span className={`px-2 py-1 text-[10px] font-black rounded uppercase tracking-widest ${match.lobbyType === 'HACKER LOBBY' || match.lobbyType === 'HACKER vs HACKER' ? 'bg-red-500/10 text-red-500' : 'bg-blue-500/10 text-blue-500'}`}>
                                                                    {match.lobbyType === 'HACKER LOBBY' || match.lobbyType === 'HACKER vs HACKER' ? 'HACKER' : 'CLEAN'}
                                                                </span>
                                                            </td>
                                                            <td className="p-4">
                                                                <span className={`px-2 py-1 text-[10px] font-black rounded uppercase tracking-widest ${match.platform === 'EMULATOR' ? 'bg-orange-500/10 text-orange-500' : 'bg-purple-500/10 text-purple-500'}`}>
                                                                    {match.platform || (activeTab === 'emulatorMatches' ? 'EMULATOR' : 'MOBILE')}
                                                                </span>
                                                            </td>
                                                            <td className="p-4 text-right flex justify-end gap-3 items-center">
                                                                <button onClick={() => setSelectedMatchForSlots({...match, docId: activeTab})} className="px-3 py-1 bg-indigo-500 hover:bg-indigo-600 text-white rounded text-xs font-bold uppercase transition-colors" title="Manage Slots">
                                                                    SLOTS
                                                                </button>
                                                                {!match.isCompleted && (
                                                                    <>
                                                                    </>
                                                                )}
                                                                {isOngoing && !match.isCompleted && (
                                                                    <button onClick={() => handleConcludeMatch(i)} className="px-3 py-1 bg-red-500 hover:bg-red-600 text-white rounded text-xs font-bold uppercase transition-colors" title="Conclude Match">
                                                                        CONCLUDE
                                                                    </button>
                                                                )}
                                                                {match.isCompleted && (
                                                                    <>
                                                                        <button onClick={() => handleToggleDummyTesting(i)} className={`px-3 py-1 ${match.isDummyTesting ? 'bg-amber-500 hover:bg-amber-600' : 'bg-gray-500 hover:bg-gray-600'} text-white rounded text-xs font-bold uppercase transition-colors`} title="Toggle Dummy Data">
                                                                            DUMMY: {match.isDummyTesting ? 'ON' : 'OFF'}
                                                                        </button>
                                                                        <button onClick={() => handleTournamentResultClick(match)} className="px-3 py-1 bg-purple-500 hover:bg-purple-600 text-white rounded text-xs font-bold uppercase transition-colors" title="View Results">
                                                                            RESULTS
                                                                        </button>
                                                                    </>
                                                                )}
                                                                <button onClick={() => handleEditSingleMatch(i)} className="p-2 bg-blue-500/10 text-blue-500 rounded hover:bg-blue-500 hover:text-white transition-colors border border-blue-500/20" title="Edit Match">
                                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path></svg>
                                                                </button>
                                                                <button onClick={() => handleDeleteMatchDirect(i)} className="p-2 bg-red-500/10 text-red-500 rounded hover:bg-red-500 hover:text-white transition-colors border border-red-500/20" title="Delete Match">
                                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path></svg>
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    )})}
                                                </tbody>
                                            </table>
                                        )}
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </>
            )}
        {banModalOpen && userToBan && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setBanModalOpen(false)}></div>
                <div className="relative w-full max-w-md bg-[#1a1a24] border border-white/10 rounded-2xl shadow-2xl overflow-hidden p-6">
                    <button onClick={() => setBanModalOpen(false)} className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors">
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                    
                    <h3 className="text-xl font-black text-white uppercase tracking-wider mb-4 border-b border-white/10 pb-4">BAN USER</h3>
                    <p className="text-sm text-gray-400 mb-6 uppercase">
                        Are you sure you want to ban <span className="text-red-500 font-bold">{userToBan.name}</span>? This will set their account to INACTIVE and block access. Please provide a reason.
                    </p>
                    
                    <div className="mb-6">
                        <label className="text-xs text-gray-500 font-bold mb-2 block uppercase tracking-wider">BAN REASON</label>
                        <input 
                            type="text" 
                            value={banReason}
                            onChange={(e) => setBanReason(e.target.value)}
                            placeholder="e.g. Using hacks in Match #123"
                            className="w-full font-black text-sm bg-black/50 border border-white/10 rounded-lg px-4 py-3 focus:outline-none focus:border-red-500 transition-colors text-white"
                            autoFocus
                        />
                    </div>
                    
                    <div className="flex gap-4">
                        <button onClick={() => setBanModalOpen(false)} className="flex-1 px-6 py-3 bg-white/5 hover:bg-white/10 text-white rounded-xl font-black text-sm uppercase transition-colors">
                            CANCEL
                        </button>
                        <button 
                            onClick={async () => {
                                if (!banReason.trim()) {
                                    alert("Please provide a ban reason.");
                                    return;
                                }
                                await setDoc(doc(db, 'users', userToBan._id), {
                                    isBanned: true,
                                    banReason: banReason,
                                    banDate: new Date().toISOString(),
                                    preBanTrustScore: userToBan.trustScore, // Save the old trust score
                                    profileData: {
                                        trustScore: 0 // Optional: can set to 0 to ensure frontend shows 0
                                    }
                                }, { merge: true });
                                
                                setAllGamersList(prev => prev.map(p => 
                                    p._id === userToBan._id 
                                        ? { ...p, isBanned: true, preBanTrustScore: userToBan.trustScore, trustScore: 0 } 
                                        : p
                                ));
                                setBanModalOpen(false);
                                alert(`${userToBan.name} has been banned.`);
                            }} 
                            className="flex-1 px-6 py-3 bg-red-600 hover:bg-red-700 text-white rounded-xl font-black text-sm uppercase transition-colors shadow-lg shadow-red-500/20"
                        >
                            BAN CONFIRM
                        </button>
                    </div>
                </div>
            </div>
        )}
        {editUserModalOpen && userToEdit && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setEditUserModalOpen(false)}></div>
                <div className="relative w-full max-w-lg bg-[#1a1a24] border border-white/10 rounded-2xl shadow-2xl overflow-hidden p-6 max-h-[90vh] overflow-y-auto">
                    <button onClick={() => setEditUserModalOpen(false)} className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors">
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                    
                    <h3 className="text-xl font-black text-white uppercase tracking-wider mb-4 border-b border-white/10 pb-4">EDIT GAMER PROFILE</h3>
                    
                    <div className="space-y-4 mb-6">
                        <div>
                            <label className="text-xs text-gray-500 font-bold mb-1 block uppercase tracking-wider">Name</label>
                            <input 
                                type="text" 
                                value={editUserData.name}
                                onChange={(e) => setEditUserData({...editUserData, name: e.target.value})}
                                className="w-full font-bold text-sm bg-black/50 border border-white/10 rounded-lg px-3 py-2 focus:outline-none focus:border-blue-500 transition-colors text-white"
                            />
                        </div>
                        <div>
                            <label className="text-xs text-gray-500 font-bold mb-1 block uppercase tracking-wider">Mobile Number</label>
                            <input 
                                type="text" 
                                value={editUserData.mobile}
                                onChange={(e) => setEditUserData({...editUserData, mobile: e.target.value})}
                                className="w-full font-bold text-sm bg-black/50 border border-white/10 rounded-lg px-3 py-2 focus:outline-none focus:border-blue-500 transition-colors text-white"
                            />
                        </div>
                        <div>
                            <label className="text-xs text-gray-500 font-bold mb-1 block uppercase tracking-wider">UPI ID</label>
                            <input 
                                type="text" 
                                value={editUserData.upiId}
                                onChange={(e) => setEditUserData({...editUserData, upiId: e.target.value})}
                                className="w-full font-bold text-sm bg-black/50 border border-white/10 rounded-lg px-3 py-2 focus:outline-none focus:border-blue-500 transition-colors text-white"
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="text-xs text-gray-500 font-bold mb-1 block uppercase tracking-wider">Game UID 1</label>
                                <div className="flex gap-2">
                                    <input 
                                        type="text" 
                                        value={editUserData.gameUid1}
                                        onChange={(e) => setEditUserData({...editUserData, gameUid1: e.target.value})}
                                        className="w-full font-bold text-sm bg-black/50 border border-white/10 rounded-lg px-3 py-2 focus:outline-none focus:border-blue-500 transition-colors text-white"
                                    />
                                    <button 
                                        onClick={() => handleFetchUidAdmin(1)} 
                                        disabled={isFetchingUid1}
                                        className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-bold text-xs uppercase transition-colors whitespace-nowrap"
                                    >
                                        {isFetchingUid1 ? '...' : 'FETCH'}
                                    </button>
                                </div>
                            </div>
                            <div>
                                <label className="text-xs text-gray-500 font-bold mb-1 block uppercase tracking-wider">Game IGN 1</label>
                                <input 
                                    type="text" 
                                    value={editUserData.gameIgn1}
                                    onChange={(e) => setEditUserData({...editUserData, gameIgn1: e.target.value})}
                                    className="w-full font-bold text-sm bg-black/50 border border-white/10 rounded-lg px-3 py-2 focus:outline-none focus:border-blue-500 transition-colors text-white"
                                />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="text-xs text-gray-500 font-bold mb-1 block uppercase tracking-wider">Game UID 2</label>
                                <div className="flex gap-2">
                                    <input 
                                        type="text" 
                                        value={editUserData.gameUid2}
                                        onChange={(e) => setEditUserData({...editUserData, gameUid2: e.target.value})}
                                        className="w-full font-bold text-sm bg-black/50 border border-white/10 rounded-lg px-3 py-2 focus:outline-none focus:border-blue-500 transition-colors text-white"
                                    />
                                    <button 
                                        onClick={() => handleFetchUidAdmin(2)} 
                                        disabled={isFetchingUid2}
                                        className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-bold text-xs uppercase transition-colors whitespace-nowrap"
                                    >
                                        {isFetchingUid2 ? '...' : 'FETCH'}
                                    </button>
                                </div>
                            </div>
                            <div>
                                <label className="text-xs text-gray-500 font-bold mb-1 block uppercase tracking-wider">Game IGN 2</label>
                                <input 
                                    type="text" 
                                    value={editUserData.gameIgn2}
                                    onChange={(e) => setEditUserData({...editUserData, gameIgn2: e.target.value})}
                                    className="w-full font-bold text-sm bg-black/50 border border-white/10 rounded-lg px-3 py-2 focus:outline-none focus:border-blue-500 transition-colors text-white"
                                />
                            </div>
                        </div>
                    </div>
                    
                    <div className="flex gap-4">
                        <button onClick={() => setEditUserModalOpen(false)} className="flex-1 px-6 py-3 bg-white/5 hover:bg-white/10 text-white rounded-xl font-black text-sm uppercase transition-colors">
                            CANCEL
                        </button>
                        <button 
                            onClick={async () => {
                                if (editUserData.gameUid1 && editUserData.gameUid2 && editUserData.gameUid1 === editUserData.gameUid2) {
                                    alert("Game UID 1 and Game UID 2 cannot be the same.");
                                    return;
                                }

                                const duplicateUid1User = editUserData.gameUid1 && allGamersList.find(gamer => 
                                    gamer._id !== userToEdit._id && 
                                    (gamer.gameUid1 === editUserData.gameUid1 || gamer.gameUid2 === editUserData.gameUid1 || gamer.profileData?.gameUid1 === editUserData.gameUid1 || gamer.profileData?.gameUid2 === editUserData.gameUid1)
                                );

                                if (duplicateUid1User) {
                                    alert(`Game UID 1 (${editUserData.gameUid1}) is already registered by another gamer (Player ID: ${duplicateUid1User.playerId}).`);
                                    return;
                                }

                                const duplicateUid2User = editUserData.gameUid2 && allGamersList.find(gamer => 
                                    gamer._id !== userToEdit._id && 
                                    (gamer.gameUid1 === editUserData.gameUid2 || gamer.gameUid2 === editUserData.gameUid2 || gamer.profileData?.gameUid1 === editUserData.gameUid2 || gamer.profileData?.gameUid2 === editUserData.gameUid2)
                                );

                                if (duplicateUid2User) {
                                    alert(`Game UID 2 (${editUserData.gameUid2}) is already registered by another gamer (Player ID: ${duplicateUid2User.playerId}).`);
                                    return;
                                }

                                const duplicateMobileUser = editUserData.mobile && allGamersList.find(gamer => 
                                    gamer._id !== userToEdit._id && 
                                    (gamer.mobile === editUserData.mobile || gamer.profileData?.mobile === editUserData.mobile)
                                );

                                if (duplicateMobileUser) {
                                    alert(`Mobile Number (${editUserData.mobile}) is already registered by another gamer (Player ID: ${duplicateMobileUser.playerId}).`);
                                    return;
                                }

                                const duplicateUpiUser = editUserData.upiId && editUserData.upiId.trim() !== '' && allGamersList.find(gamer => 
                                    gamer._id !== userToEdit._id && 
                                    (gamer.upiId === editUserData.upiId || gamer.profileData?.upiId === editUserData.upiId)
                                );

                                if (duplicateUpiUser) {
                                    alert(`UPI ID (${editUserData.upiId}) is already registered by another gamer (Player ID: ${duplicateUpiUser.playerId}).`);
                                    return;
                                }

                                try {
                                    await setDoc(doc(db, 'users', userToEdit._id), {
                                        name: editUserData.name,
                                        mobile: editUserData.mobile,
                                        upiId: editUserData.upiId,
                                        gameUid1: editUserData.gameUid1,
                                        gameIgn1: editUserData.gameIgn1,
                                        gameUid2: editUserData.gameUid2,
                                        gameIgn2: editUserData.gameIgn2,
                                        profileData: {
                                            name: editUserData.name,
                                            mobile: editUserData.mobile,
                                            upiId: editUserData.upiId,
                                            gameUid1: editUserData.gameUid1,
                                            gameIgn1: editUserData.gameIgn1,
                                            gameUid2: editUserData.gameUid2,
                                            gameIgn2: editUserData.gameIgn2
                                        }
                                    }, { merge: true });
                                    
                                    setEditUserModalOpen(false);
                                    alert(`${editUserData.name}'s profile has been updated successfully.`);
                                } catch (error) {
                                    console.error("Error updating user:", error);
                                    alert("Failed to update user profile.");
                                }
                            }} 
                            className="flex-1 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-black text-sm uppercase transition-colors shadow-lg shadow-blue-500/20"
                        >
                            SAVE CHANGES
                        </button>
                    </div>
                </div>
            </div>
        )}

        {idPassModalOpen && matchForIdPass && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setIdPassModalOpen(false)}></div>
                <div className="relative w-full max-w-sm bg-[#1a1a24] border border-white/10 rounded-2xl shadow-2xl overflow-hidden p-6">
                    <button onClick={() => setIdPassModalOpen(false)} className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors">
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                    
                    <h3 className="text-xl font-black text-white uppercase tracking-wider mb-2 border-b border-white/10 pb-4">PUBLISH ID/PASS</h3>
                    <p className="text-sm text-gray-400 mb-6 uppercase">
                        Enter Room ID and Password for <span className="text-pink-500 font-bold">{matchForIdPass.title}</span>
                    </p>
                    
                    <div className="mb-4">
                        <label className="text-xs text-gray-500 font-bold mb-2 block uppercase tracking-wider">ROOM ID</label>
                        <input 
                            type="text" 
                            value={idPassData.roomId}
                            onChange={(e) => setIdPassData({...idPassData, roomId: e.target.value})}
                            placeholder="Enter Room ID"
                            className="w-full font-black text-sm bg-black/50 border border-white/10 rounded-lg px-4 py-3 focus:outline-none focus:border-blue-500 transition-colors text-white"
                            autoFocus
                        />
                    </div>

                    <div className="mb-6">
                        <label className="text-xs text-gray-500 font-bold mb-2 block uppercase tracking-wider">PASSWORD</label>
                        <input 
                            type="text" 
                            value={idPassData.roomPassword}
                            onChange={(e) => setIdPassData({...idPassData, roomPassword: e.target.value})}
                            placeholder="Enter Password"
                            className="w-full font-black text-sm bg-black/50 border border-white/10 rounded-lg px-4 py-3 focus:outline-none focus:border-blue-500 transition-colors text-white"
                        />
                    </div>
                    
                    <div className="flex gap-4">
                        <button onClick={() => setIdPassModalOpen(false)} className="flex-1 px-6 py-3 bg-white/5 hover:bg-white/10 text-white rounded-xl font-black text-sm uppercase transition-colors">
                            CANCEL
                        </button>
                        <button 
                            onClick={handlePublishIdPass} 
                            className="flex-1 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-black text-sm uppercase transition-colors shadow-lg shadow-blue-500/20"
                        >
                            PUBLISH
                        </button>
                    </div>
                </div>
            </div>
        )}
        </div>
    );
}
